import { useState, useEffect, useMemo } from "react";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { GoogleLoginButton } from "@/components/GoogleLoginButton";
import {
  MessageSquare,
  Bot,
  BarChart3,
  Plug,
  Sparkles,
  Eye,
  EyeOff,
  ChevronDown,
  ChevronUp,
  Check,
  ArrowRight,
  Megaphone,
  Video,
  Zap,
  Building2,
  Briefcase,
  User as UserIcon,
} from "lucide-react";

// ─── Constants ───

const FEATURE_ICONS = [MessageSquare, Bot, BarChart3, Plug, Sparkles] as const;
const FEATURE_KEYS = [
  "contentCreator",
  "aiAssistant",
  "marketingAnalyst",
  "integrations",
  "ideaBank",
] as const;

const COMPANY_SIZE_VALUES = ["1", "2-5", "6-10", "11-50", "51+"] as const;
const HOW_DID_YOU_HEAR_VALUES = [
  "facebook",
  "google",
  "youtube",
  "referral",
  "podcast",
  "event",
  "other",
] as const;

// ─── Onboarding goals (2 questions) ───

type Tier = "start" | "pro" | "expert";
type Cycle = "monthly" | "annual";
type Vertical = "lead_generation" | "ecommerce" | "content_creation";
type UseCase = "ads" | "content" | "crm" | "automation" | "all";
type Persona = "agency" | "leadgen" | "creator";

const USE_CASE_OPTIONS: { value: UseCase; Icon: typeof Megaphone }[] = [
  { value: "ads", Icon: Megaphone },
  { value: "content", Icon: Video },
  { value: "crm", Icon: BarChart3 },
  { value: "automation", Icon: Zap },
  { value: "all", Icon: Sparkles },
];

const PERSONA_OPTIONS: { value: Persona; Icon: typeof Building2 }[] = [
  { value: "agency", Icon: Building2 },
  { value: "leadgen", Icon: Briefcase },
  { value: "creator", Icon: UserIcon },
];

const PERSONA_TO_VERTICAL: Record<Persona, Vertical> = {
  agency: "lead_generation",
  leadgen: "lead_generation",
  creator: "content_creation",
};

const TIER_DISPLAY: Record<
  Tier,
  {
    name: string;
    priceMonthly: string;
    priceAnnual: string;
    tagline: string;
    bullets: string[];
    popular?: boolean;
  }
> = {
  start: {
    name: "Start",
    priceMonthly: "499 zł",
    priceAnnual: "4 990 zł",
    tagline: "Cała maszyna do tworzenia reklam i contentu",
    bullets: [
      "Twórca reklam graficznych",
      "Twórca reklam video",
      "Twórca skryptów reklam + VSL",
      "Twórca filmów na YouTube + rolek",
      "Kreator kampanii reklamowych z AI",
      "Analityk AI + Monitoring AI",
      "Wsparcie Intercom",
    ],
  },
  pro: {
    name: "Pro",
    priceMonthly: "1 499 zł",
    priceAnnual: "14 990 zł",
    tagline: "Wszystko ze Start + integracja z CRM",
    popular: true,
    bullets: [
      "Wszystkie funkcje z Planu Start",
      "Połączenie z CRM (LeadConnector / HubSpot / Calendly)",
      "Priorytetowe wsparcie Intercom",
    ],
  },
  expert: {
    name: "Expert",
    priceMonthly: "5 999 zł",
    priceAnnual: "59 990 zł",
    tagline: "Wszystko z Pro + pełna ścieżka klienta",
    bullets: [
      "Wszystkie funkcje z Planu Pro",
      "Server-side CAPI (Meta)",
      "Advanced tracking — pełna ścieżka klienta",
      "Dedykowany opiekun konta",
    ],
  },
};

// ─── Step 1: Registration ───

function StepRegister({ onComplete }: { onComplete: () => void }) {
  const { t } = useTranslation();
  const { register, isRegisterLoading } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    referralCode: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showReferral, setShowReferral] = useState(false);

  /* Auto-fill referral code from ?fpr= URL param and detect extended trial */
  const isExtendedFromUrl =
    new URLSearchParams(window.location.search).get("trialType") === "extended";

  /* ?layout=clean (ruch z reklam): sam wysrodkowany card, Google na gorze, bez panelu marketingowego */
  const isCleanLayout = useMemo(
    () => new URLSearchParams(window.location.search).get("layout") === "clean",
    []
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fpr = params.get("fpr");
    if (fpr) {
      setFormData((prev) => ({ ...prev, referralCode: fpr }));
      setShowReferral(true);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.password.length < 8) {
      toast({
        title: t("trial.errors.passwordTooShort"),
        description: t("trial.errors.passwordMinLength"),
        variant: "destructive",
      });
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      toast({ title: t("trial.errors.passwordsDoNotMatch"), variant: "destructive" });
      return;
    }
    try {
      const hasReferral = Boolean(formData.referralCode);
      const isExtended = hasReferral || isExtendedFromUrl;
      await register({
        username: formData.email.split("@")[0],
        email: formData.email,
        password: formData.password,
        ...(hasReferral ? { referralCode: formData.referralCode } : {}),
        ...(isExtended ? { trialType: "extended" as const } : {}),
      });
      onComplete();
    } catch (error) {
      const msg = (error as Error)?.message || "";
      const exists = msg.includes("już istnieje") || msg.toLowerCase().includes("already exists");
      toast({
        title: exists ? t("trial.errors.accountExists") : t("trial.errors.registerError"),
        description: exists
          ? t("trial.errors.accountExistsDesc")
          : msg || t("trial.errors.tryAgain"),
        variant: "destructive",
      });
    }
  };

  const features = FEATURE_KEYS.map((key, i) => ({
    Icon: FEATURE_ICONS[i],
    title: t(`trial.features.${key}.title`),
    description: t(`trial.features.${key}.description`),
  }));

  const emailFormJsx = (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email" className="text-white/70">
          {t("trial.register.email")}
        </Label>
        <Input
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
          required
          className="bg-white/[0.06] border-white/[0.1] text-white"
          placeholder={t("trial.register.emailPlaceholder")}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="password" className="text-white/70">
          {t("trial.register.password")}
        </Label>
        <div className="relative">
          <Input
            id="password"
            name="password"
            type={showPassword ? "text" : "password"}
            value={formData.password}
            onChange={handleInputChange}
            required
            className="bg-white/[0.06] border-white/[0.1] text-white pr-10"
            placeholder={t("trial.register.passwordPlaceholder")}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60"
          >
            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="confirmPassword" className="text-white/70">
          {t("trial.register.confirmPassword")}
        </Label>
        <Input
          id="confirmPassword"
          name="confirmPassword"
          type={showPassword ? "text" : "password"}
          value={formData.confirmPassword}
          onChange={handleInputChange}
          required
          className="bg-white/[0.06] border-white/[0.1] text-white"
          placeholder={t("trial.register.confirmPasswordPlaceholder")}
        />
      </div>
      <button
        type="button"
        onClick={() => setShowReferral(!showReferral)}
        className="text-sm text-white/50 hover:text-white/70 flex items-center gap-1"
      >
        {showReferral ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}{" "}
        {t("trial.register.referralToggle")}
      </button>
      {showReferral && (
        <Input
          name="referralCode"
          value={formData.referralCode}
          onChange={handleInputChange}
          className="bg-white/[0.06] border-white/[0.1] text-white"
          placeholder={t("trial.register.referralPlaceholder")}
        />
      )}
      <Button
        type="submit"
        disabled={isRegisterLoading}
        className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white h-11 text-base font-semibold"
      >
        {isRegisterLoading ? t("trial.register.creating") : t("trial.register.submit")}
      </Button>
    </form>
  );

  const dividerJsx = (
    <div className="relative">
      <div className="absolute inset-0 flex items-center">
        <span className="w-full border-t border-white/[0.1]" />
      </div>
      <div className="relative flex justify-center text-xs uppercase">
        <span className="bg-card px-2 text-white/40">{t("trial.register.or")}</span>
      </div>
    </div>
  );

  const googleJsx = <GoogleLoginButton onSuccess={() => onComplete()} />;

  const cardJsx = (
    <Card className="w-full max-w-md bg-card backdrop-blur-sm border-white/[0.08]">
      <CardContent className="p-8">
        <div className="text-center mb-6">
          {isCleanLayout && (
            <img
              src="https://pub-9c0a0846a5c24484849bf7e3f13e4239.r2.dev/viralpartners/logo-800w.webp"
              alt="Viral Partners Logo"
              className="h-10 object-contain mx-auto mb-6"
            />
          )}
          <h2 className="text-heading-2 font-extrabold text-white mb-3">
            {t("trial.register.title")}
          </h2>
          <p className="text-white/50 text-sm">{t("trial.register.subtitle")}</p>
        </div>
        {isCleanLayout ? (
          <>
            {googleJsx}
            <div className="my-6">{dividerJsx}</div>
            {emailFormJsx}
          </>
        ) : (
          <>
            {emailFormJsx}
            <div className="mt-6">
              {dividerJsx}
              <div className="mt-4">{googleJsx}</div>
            </div>
          </>
        )}
        <p className="mt-6 text-center text-sm text-white/40">
          {t("trial.register.haveAccount")}{" "}
          <button
            type="button"
            onClick={() => setLocation("/")}
            className="text-red-400 hover:text-red-300 font-medium"
          >
            {t("trial.register.signIn")}
          </button>
        </p>
      </CardContent>
    </Card>
  );

  if (isCleanLayout) {
    return (
      <div className="min-h-[100dvh] bg-[#030407] flex items-center justify-center px-6 py-12">
        {cardJsx}
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-background flex flex-col lg:flex-row">
      {/* Left — features */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-16 xl:px-24">
        <div className="max-w-lg mx-auto lg:mx-0">
          <img
            src="https://pub-9c0a0846a5c24484849bf7e3f13e4239.r2.dev/viralpartners/logo-800w.webp"
            alt="Viral Partners Logo"
            className="h-12 object-contain mb-8"
          />
          <h1 className="text-heading-1 sm:text-heading-1 font-extrabold text-white leading-tight mb-4">
            Viral Partners –{" "}
            <span className="bg-gradient-to-r from-red-400 to-red-300 bg-clip-text text-transparent">
              MarketingOS
            </span>
          </h1>
          <p className="text-white/60 text-lg mb-10">{t("trial.heroTagline")}</p>
          <div className="space-y-6">
            {features.map((f) => (
              <div key={f.title} className="flex items-start gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-red-500/20 to-red-600/10 border border-red-500/20 flex items-center justify-center">
                  <f.Icon className="w-5 h-5 text-red-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">{f.title}</h3>
                  <p className="text-white/50 text-sm">{f.description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 p-4 rounded-xl bg-gradient-to-r from-red-500/10 to-transparent border border-red-500/20">
            <p className="text-white/80 text-sm">
              <span className="font-semibold text-red-400">{t("trial.socialProof.headline")}</span>{" "}
              {t("trial.socialProof.body")}
            </p>
          </div>
        </div>
      </div>
      {/* Right — form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 lg:px-16">{cardJsx}</div>
    </div>
  );
}

// ─── Step 2: Account info ───

function StepProfile({ onComplete }: { onComplete: () => void }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    companyName: "",
    companySize: "1",
    howDidYouHear: "",
  });

  const profileMutation = useMutation({
    mutationFn: async (data: typeof form) => apiRequest("POST", "/api/onboarding/profile", data),
    onSuccess: () => onComplete(),
    onError: () => toast({ title: t("trial.errors.saveAgain"), variant: "destructive" }),
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const companySizes = COMPANY_SIZE_VALUES.map((v) => ({
    value: v,
    label: t(`trial.companySize.${v}`),
  }));
  const howOptions = HOW_DID_YOU_HEAR_VALUES.map((v) => ({
    value: v,
    label: t(`trial.howDidYouHear.${v}`),
  }));

  return (
    <div className="min-h-[100dvh] bg-background flex items-center justify-center px-4 pt-24 pb-12">
      <Card className="w-full max-w-lg bg-card backdrop-blur-sm border-white/[0.08]">
        <CardContent className="p-8">
          <div className="text-center mb-8">
            <h2 className="text-heading-2 font-extrabold text-white mb-3">
              {t("trial.profile.title")}
            </h2>
            <p className="text-white/50 text-sm">{t("trial.profile.subtitle")}</p>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-dense">
              <div className="space-y-2">
                <Label className="text-white/70">
                  {t("trial.profile.firstName")} <span className="text-red-400">*</span>
                </Label>
                <Input
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  className="bg-white/[0.06] border-white/[0.1] text-white"
                  placeholder={t("trial.profile.firstName")}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">
                  {t("trial.profile.lastName")} <span className="text-red-400">*</span>
                </Label>
                <Input
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  className="bg-white/[0.06] border-white/[0.1] text-white"
                  placeholder={t("trial.profile.lastName")}
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">
                {t("trial.profile.phone")} <span className="text-red-400">*</span>
              </Label>
              <div className="flex gap-2">
                <div className="flex items-center px-3 rounded-md bg-white/[0.06] border border-white/[0.1] text-white/60 text-sm whitespace-nowrap">
                  PL +48
                </div>
                <Input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className="bg-white/[0.06] border-white/[0.1] text-white flex-1"
                  placeholder={t("trial.profile.phone")}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">{t("trial.profile.companyName")}</Label>
              <Input
                name="companyName"
                value={form.companyName}
                onChange={handleChange}
                className="bg-white/[0.06] border-white/[0.1] text-white"
                placeholder={t("trial.profile.companyNamePlaceholder")}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">{t("trial.profile.companySize")}</Label>
              <select
                name="companySize"
                value={form.companySize}
                onChange={handleChange}
                className="w-full h-10 rounded-md bg-white/[0.06] border border-white/[0.1] text-white px-3 text-sm"
              >
                {companySizes.map((o) => (
                  <option key={o.value} value={o.value} className="bg-card">
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label className="text-white/70">
                {t("trial.profile.howDidYouHear")} <span className="text-red-400">*</span>
              </Label>
              <select
                name="howDidYouHear"
                value={form.howDidYouHear}
                onChange={handleChange}
                className="w-full h-10 rounded-md bg-white/[0.06] border border-white/[0.1] text-white px-3 text-sm"
              >
                <option value="" className="bg-card">
                  {t("trial.profile.selectOption")}
                </option>
                {howOptions.map((o) => (
                  <option key={o.value} value={o.value} className="bg-card">
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <Button
              onClick={() => {
                if (
                  !form.firstName.trim() ||
                  !form.lastName.trim() ||
                  !form.phone.trim() ||
                  !form.howDidYouHear
                ) {
                  toast({ title: t("trial.errors.fillAllRequired"), variant: "destructive" });
                  return;
                }
                profileMutation.mutate(form);
              }}
              disabled={profileMutation.isPending}
              className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white h-11 text-base font-semibold mt-2"
            >
              {profileMutation.isPending ? t("trial.profile.saving") : t("trial.profile.continue")}
              {!profileMutation.isPending && <ArrowRight className="w-4 h-4 ml-2" />}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Step 3: Goals — 2 questions (use-case + persona) ───

function StepGoals({
  onComplete,
}: {
  onComplete: (goals: { useCase: UseCase; persona: Persona }) => void;
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [useCase, setUseCase] = useState<UseCase | null>(null);
  const [persona, setPersona] = useState<Persona | null>(null);

  const saveMutation = useMutation({
    mutationFn: async (data: { useCase: UseCase; persona: Persona }) =>
      apiRequest("POST", "/api/onboarding/goals", data),
    onSuccess: (_res, vars) => onComplete(vars),
    onError: () => toast({ title: t("trial.errors.saveAgain"), variant: "destructive" }),
  });

  return (
    <div className="min-h-[100dvh] bg-background flex items-center justify-center px-4 pt-24 pb-12">
      <Card className="w-full max-w-2xl bg-card backdrop-blur-sm border-white/[0.08]">
        <CardContent className="p-8">
          <div className="text-center mb-8">
            <h2 className="text-heading-2 font-extrabold text-white mb-2">
              {t("trial.goals.title")}
            </h2>
            <p className="text-white/50 text-sm">{t("trial.goals.subtitle")}</p>
          </div>

          {/* Q1 — use case */}
          <div className="mb-8">
            <h3 className="text-white font-semibold mb-3">{t("trial.goals.q1")}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {USE_CASE_OPTIONS.map(({ value, Icon }) => {
                const active = useCase === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setUseCase(value)}
                    className={`flex items-center gap-3 p-4 rounded-xl border text-left transition-all ${active ? "border-red-500 bg-red-500/10" : "border-white/[0.1] bg-white/[0.03] hover:bg-white/[0.06]"}`}
                  >
                    <Icon
                      className={`w-5 h-5 flex-shrink-0 ${active ? "text-red-400" : "text-white/50"}`}
                    />
                    <span className="text-white text-sm font-medium">
                      {t(`trial.useCase.${value}`)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Q2 — persona */}
          <div className="mb-8">
            <h3 className="text-white font-semibold mb-3">{t("trial.goals.q2")}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {PERSONA_OPTIONS.map(({ value, Icon }) => {
                const active = persona === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setPersona(value)}
                    className={`flex flex-col items-start gap-2 p-4 rounded-xl border text-left transition-all ${active ? "border-red-500 bg-red-500/10" : "border-white/[0.1] bg-white/[0.03] hover:bg-white/[0.06]"}`}
                  >
                    <Icon className={`w-5 h-5 ${active ? "text-red-400" : "text-white/50"}`} />
                    <span className="text-white text-sm font-semibold">
                      {t(`trial.persona.${value}`)}
                    </span>
                    <span className="text-white/45 text-xs">{t(`trial.persona.${value}Desc`)}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <Button
            onClick={() => {
              if (!useCase || !persona) {
                toast({ title: t("trial.errors.fillAllRequired"), variant: "destructive" });
                return;
              }
              saveMutation.mutate({ useCase, persona });
            }}
            disabled={saveMutation.isPending}
            className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white h-11 text-base font-semibold"
          >
            {saveMutation.isPending ? t("trial.goals.saving") : t("trial.goals.continue")}
            {!saveMutation.isPending && <ArrowRight className="w-4 h-4 ml-2" />}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Step 4: Plan selection → Stripe ───

/** Macierz porownania planow — wartosci zgodne z cennikiem na landingu (viral.partners/fbpage). */
const PLAN_COMPARISON_ROWS: {
  label: string;
  values: [string | boolean, string | boolean, string | boolean];
}[] = [
  { label: "Użytkownicy", values: ["1", "3", "10"] },
  { label: "Tokeny AI / miesiąc", values: ["2 000", "6 000", "18 000"] },
  { label: "Kampanie reklamowe", values: ["Nielimitowane", "Nielimitowane", "Nielimitowane"] },
  { label: "Grafiki AI / miesiąc", values: ["~120", "~360", "~1 080"] },
  { label: "Reklam tworzonych jednorazowo", values: ["20", "20", "20"] },
  {
    label: "Bulk upload (drag & drop)",
    values: ["100 reklam na raz", "100 reklam na raz", "100 reklam na raz"],
  },
  { label: "Planowanie postów (Instagram / TikTok / Facebook)", values: [true, true, true] },
  { label: "Twórca reklam graficznych i video", values: [true, true, true] },
  { label: "Twórca skryptów, VSL, YouTube i rolek", values: [true, true, true] },
  { label: "Twórca strategii marketingowej", values: [true, true, true] },
  { label: "Kreator kampanii reklamowych z AI", values: [true, true, true] },
  { label: "Analityk AI + Monitoring AI", values: [true, true, true] },
  { label: "Połączenie z CRM (LeadConnector / HubSpot / Calendly)", values: [false, true, true] },
  { label: "Server-side CAPI (Meta)", values: [false, false, true] },
  { label: "Advanced tracking — pełna ścieżka klienta", values: [false, false, true] },
  { label: "Dedykowany opiekun konta", values: [false, false, true] },
  { label: "Wsparcie Intercom", values: ["Standard", "Priorytetowe", "Priorytetowe z SLA"] },
];

function StepPlan({
  persona,
  onSelect,
  onSkip,
  isSkipping,
}: {
  persona: Persona | null;
  onSelect: (tier: Tier, cycle: Cycle, vertical: Vertical) => void;
  onSkip: () => void;
  isSkipping: boolean;
}) {
  const { t } = useTranslation();
  const [cycle, setCycle] = useState<Cycle>("monthly");
  const [showComparison, setShowComparison] = useState(false);
  const vertical: Vertical = persona ? PERSONA_TO_VERTICAL[persona] : "lead_generation";
  const tiers: Tier[] = ["start", "pro", "expert"];

  return (
    <div className="min-h-[100dvh] bg-background flex items-center justify-center p-4 py-12">
      <div className="w-full max-w-5xl">
        <div className="text-center mb-8">
          <h2 className="text-heading-2 font-extrabold text-white mb-2">{t("trial.plan.title")}</h2>
          <p className="text-white/50 text-sm mb-6">{t("trial.plan.subtitle")}</p>
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-white/[0.06] border border-white/[0.1]">
            <button
              type="button"
              onClick={() => setCycle("monthly")}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${cycle === "monthly" ? "bg-red-500 text-white" : "text-white/60"}`}
            >
              {t("trial.plan.monthly")}
            </button>
            <button
              type="button"
              onClick={() => setCycle("annual")}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${cycle === "annual" ? "bg-red-500 text-white" : "text-white/60"}`}
            >
              {t("trial.plan.annual")}
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-green-500/20 text-green-400">
                {t("trial.plan.annualBadge")}
              </span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {tiers.map((tier) => {
            const d = TIER_DISPLAY[tier];
            const price = cycle === "monthly" ? d.priceMonthly : d.priceAnnual;
            const unit = cycle === "monthly" ? "/msc" : "/rok";
            return (
              <div
                key={tier}
                className={`relative rounded-2xl border p-6 flex flex-col ${d.popular ? "border-red-500 bg-red-500/[0.06]" : "border-white/[0.1] bg-white/[0.03]"}`}
              >
                {d.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-red-500 text-white text-xs font-bold uppercase tracking-wide">
                    {t("trial.plan.popular")}
                  </span>
                )}
                <h3 className="text-white text-xl font-extrabold">{d.name}</h3>
                <p className="text-white/50 text-sm mt-1 mb-4 min-h-[40px]">{d.tagline}</p>
                <div className="mb-4">
                  <span className="text-white text-3xl font-extrabold">{price}</span>
                  <span className="text-white/50 text-sm"> {unit}</span>
                  <div className="text-white/40 text-xs mt-1">netto + 23% VAT</div>
                </div>
                <Button
                  onClick={() => onSelect(tier, cycle, "lead_generation")}
                  className={`w-full h-11 font-semibold mb-5 ${d.popular ? "bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white" : "bg-white/[0.08] hover:bg-white/[0.14] text-white"}`}
                >
                  {t("trial.plan.choose", { name: d.name })}
                </Button>
                <ul className="space-y-2 mt-auto">
                  {d.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-white/70 text-sm">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
          <div className="relative rounded-2xl border border-emerald-500 bg-emerald-500/[0.06] p-6 flex flex-col">
            {vertical === "content_creation" && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-emerald-500 text-black text-xs font-bold uppercase tracking-wide">
                Rekomendowany
              </span>
            )}
            <h3 className="text-white text-xl font-extrabold">Organic</h3>
            <p className="text-white/50 text-sm mt-1 mb-4 min-h-[40px]">
              Cała zakładka Organic + daily viral Inspiracje
            </p>
            <div className="mb-4">
              <span className="text-white text-3xl font-extrabold">
                {cycle === "monthly" ? "99 zł" : "950 zł"}
              </span>
              <span className="text-white/50 text-sm">
                {" "}
                {cycle === "monthly" ? "/msc" : "/rok"}
              </span>
              <div className="text-white/40 text-xs mt-1">netto + 23% VAT · 900 tokenów/mies</div>
            </div>
            <Button
              onClick={() => onSelect("start", cycle, "content_creation")}
              className="w-full h-11 font-semibold mb-5 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white"
            >
              Wybierz Organic
            </Button>
            <ul className="space-y-2 mt-auto">
              {[
                "Feed Inspiracji (daily viral)",
                "Generator skryptów i hooków",
                "Twórca reelsów",
                "Generacja grafik AI",
                "Scheduler publikacji",
                "Asystent wiadomości + Analityka",
              ].map((b) => (
                <li key={b} className="flex items-start gap-2 text-white/70 text-sm">
                  <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-5 flex justify-center">
          <button
            type="button"
            onClick={onSkip}
            disabled={isSkipping}
            className="w-full max-w-md h-12 rounded-xl bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.12] text-white/80 text-sm font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSkipping ? t("trial.plan.skipLoading") : t("trial.plan.skip")}
          </button>
        </div>

        <div className="text-center mt-6">
          <button
            type="button"
            onClick={() => setShowComparison((prev) => !prev)}
            className="text-white/50 hover:text-white/80 text-sm inline-flex items-center gap-1"
          >
            {showComparison ? (
              <ChevronUp className="w-3.5 h-3.5" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5" />
            )}
            {showComparison ? t("trial.plan.compareHide") : t("trial.plan.compareToggle")}
          </button>
        </div>

        {showComparison && (
          <div className="mt-6 rounded-2xl border border-white/[0.1] bg-white/[0.02] overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.1]">
                  <th className="text-left text-white/50 font-medium px-4 py-3">Funkcja</th>
                  {tiers.map((tier) => (
                    <th key={tier} className="text-center text-white font-bold px-4 py-3">
                      {TIER_DISPLAY[tier].name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PLAN_COMPARISON_ROWS.map((row) => (
                  <tr key={row.label} className="border-b border-white/[0.06] last:border-0">
                    <td className="text-white/70 px-4 py-2.5">{row.label}</td>
                    {row.values.map((value, i) => (
                      <td key={tiers[i]} className="text-center px-4 py-2.5">
                        {value === true ? (
                          <Check className="w-4 h-4 text-green-400 inline" />
                        ) : value === false ? (
                          <span className="text-white/25">—</span>
                        ) : (
                          <span className="text-white/80">{value}</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Step progress ───

function StepProgress({ step, total }: { step: number; total: number }) {
  return (
    <div className="fixed top-0 left-0 right-0 z-50 flex justify-center pt-6 pointer-events-none">
      <div className="flex items-center gap-2">
        {Array.from({ length: total }, (_, i) => (
          <div
            key={i}
            className={`h-1.5 rounded-full transition-all duration-300 ${i < step ? "w-8 bg-red-500" : i === step ? "w-8 bg-red-400" : "w-8 bg-white/20"}`}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Main ───

const VALID_TIERS_FROM_LP: Record<string, Tier> = {
  starter: "start",
  start: "start",
  pro: "pro",
  expert: "expert",
};

const VALID_VERTICALS_FROM_LP = new Set(["lead_generation", "ecommerce", "content_creation"]);

function readSelectedPlanFromUrl(): { tier: Tier; cycle: Cycle; vertical: Vertical } | null {
  const params = new URLSearchParams(window.location.search);
  const planParam = params.get("plan");
  if (!planParam) return null;
  const tier = VALID_TIERS_FROM_LP[planParam.toLowerCase()];
  if (!tier) return null;
  const cycleParam = params.get("cycle")?.toLowerCase();
  const cycle: Cycle = cycleParam === "annual" || cycleParam === "yearly" ? "annual" : "monthly";
  const verticalParam = params.get("vertical")?.toLowerCase() ?? "lead_generation";
  const vertical = VALID_VERTICALS_FROM_LP.has(verticalParam)
    ? (verticalParam as Vertical)
    : "lead_generation";
  return { tier, cycle, vertical };
}

export default function TrialPage() {
  const [step, setStep] = useState(0);
  const [persona, setPersona] = useState<Persona | null>(null);
  const [redirectingToCheckout, setRedirectingToCheckout] = useState(false);
  const { user, isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const selectedPlan = typeof window !== "undefined" ? readSelectedPlanFromUrl() : null;

  useEffect(() => {
    if (isAuthenticated && step === 0 && !selectedPlan && !redirectingToCheckout) setStep(1);
  }, [isAuthenticated, step, selectedPlan, redirectingToCheckout]);

  const completeMutation = useMutation({
    mutationFn: async () => apiRequest("POST", "/api/onboarding/complete"),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] }),
  });

  const startCheckout = async (plan: { tier: Tier; cycle: Cycle; vertical: Vertical }) => {
    setRedirectingToCheckout(true);
    try {
      const payload = { ...plan, cycle: plan.cycle === "annual" ? "yearly" : "monthly" };
      const data = (await apiRequest("POST", "/api/billing/create-checkout", payload)) as {
        url?: string;
      };
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error("Brak URL Stripe Checkout");
    } catch (err) {
      toast({
        title: "Nie udało się otworzyć płatności",
        description: (err as Error)?.message || "Spróbuj ponownie z poziomu konta.",
        variant: "destructive",
      });
      setRedirectingToCheckout(false);
    }
  };

  const handleRegisterComplete = async () => {
    if (!selectedPlan) {
      setStep(1);
      return;
    }
    await startCheckout(selectedPlan);
  };

  const handleSkipPlan = () => {
    // Idiotoodporny onboarding: po wejściu na trial wrzucamy usera PROSTO w szybki
    // tor kreatora kampanii (fastTrackMode na pierwszej wizycie), zamiast na pełny
    // dashboard. Brama "Połącz Meta" → URL → twórca → Uruchom (paywall jeśli brak
    // płatnej subskrypcji).
    completeMutation.mutate();
    setLocation("/chat/campaign-creator");
  };

  if (redirectingToCheckout) {
    return (
      <div className="h-screen flex items-center justify-center bg-background text-white">
        <div className="text-center">
          <div className="text-2xl font-bold mb-2">Przekierowuję do płatności…</div>
          <div className="text-sm text-white/60">Zaraz wylądujesz na Stripe Checkout.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] overflow-y-auto [-webkit-overflow-scrolling:touch]">
      {step > 0 && <StepProgress step={step} total={4} />}
      {step === 0 && <StepRegister onComplete={handleRegisterComplete} />}
      {step === 1 && <StepProfile onComplete={() => setStep(2)} />}
      {step === 2 && (
        <StepGoals
          onComplete={(goals) => {
            setPersona(goals.persona);
            setStep(3);
          }}
        />
      )}
      {step === 3 && (
        <StepPlan
          persona={persona}
          onSelect={(tier, cycle, vertical) => startCheckout({ tier, cycle, vertical })}
          onSkip={handleSkipPlan}
          isSkipping={completeMutation.isPending}
        />
      )}
    </div>
  );
}
